import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BASE_URL } from '@portal/shared/constants/tokens';
import { ConfigurationService, OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

enum ErrorCode {
  InvalidGrant = 'invalid_grant'
}

enum AuthUrl {
  TokenRefresh = 'protocol/openid-connect/token'
}

interface AuthError {
  error: ErrorCode;
  description: string;
}

interface ErrorHandler {
  name: string;
  shouldHandle(request: HttpRequest<unknown>, error: AuthError): boolean;
  handle(): void;
}

export const isInvalidGrantError = (url: string, error: AuthError): boolean => {
  return url.endsWith(AuthUrl.TokenRefresh) && error?.error === ErrorCode.InvalidGrant;
};

export function requestErrorHandlerInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const baseUrl = inject(BASE_URL);
  const router = inject(Router);
  const config = inject(ConfigurationService);
  const oidcSecurityService = inject(OidcSecurityService);
  const errorHandlers: ErrorHandler[] = [
    {
      name: 'Refresh token error',
      shouldHandle: (req: HttpRequest<unknown>, error: AuthError): boolean => {
        return isInvalidGrantError(req.url, error);
      },
      handle: (): void => {
        config
          .getOpenIDConfiguration()
          .pipe(
            switchMap(config => {
              const redirectUrl = router.url ? `${baseUrl}${router.url}/callback` : config?.redirectUrl;

              //todo: fix navigation to the previous location
              /* 
                Notes: logoffInternal(logoutAuthOptions, endSessionUrl, config, allConfigs) builds correct endSessionUrl and navigates by it.
                But eventually, we are navigated to the baseUrl (app root '/'), and the navigation happens inside the autoLoginPartialRoutesGuard.
                The resulting url will have the default redirect_uri.
              */
              return oidcSecurityService.logoff(config?.configId, {
                customParams: {
                  redirect_uri: redirectUrl!,
                  post_logout_redirect_uri: redirectUrl!
                }
              });
            })
          )
          .subscribe();
      }
    }
  ];

  return next(request).pipe(
    catchError(err => {
      const handler = errorHandlers.find(handler => handler.shouldHandle(request, err.error));
      if (handler) {
        handler.handle();
      }

      return throwError(() => err);
    })
  );
}
